import axios from 'axios';
import { keycloakService } from '@/services/keycloak.service';
import { iniciarCarregamento, finalizarCarregamento } from '@/plugins/loading';
import { concluiAcaoDoUsuario, registrarAcaoConcluida } from '@/composables/convite-pesquisa';

const apiUrl = import.meta.env.VITE_API_URL;

if (apiUrl) {
  axios.defaults.baseURL = apiUrl.replace(/\/+$/, '');
}

/**
 * Teto de espera de qualquer chamada. Sem isto o axios espera para SEMPRE: o reenvio da tela de
 * Andamento chegava a segurar a aba por minutos, sem rodinha que terminasse nem erro que
 * aparecesse — indistinguível de um botão quebrado.
 *
 * 250s e não 30s de propósito: o envio ao TISS atravessa a fila e o WS da Benner, cujo timeout
 * interno é de 240s. Um teto menor aqui faria o navegador desistir enquanto o servidor ainda
 * processa, e o usuário reenviaria algo que estava indo bem — que é justamente como se cria guia
 * duplicada. É o mesmo valor do readTimeout do Feign no backend.
 */
axios.defaults.timeout = 250_000;

axios.interceptors.request.use(async (config) => {
  try {
    await keycloakService.keycloak.updateToken(70);
  } catch (error) {
    keycloakService.keycloak.logout();
    return Promise.reject(error);
  }

  const token = keycloakService.keycloak.token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Vários controllers do backend leem o prestador de @RequestHeader("X-Usuario"), com default
  // "API_USER". Como o front nunca enviava, o default vencia e a consulta filtrava por um
  // prestador inexistente — dado vazio, sem erro. Enviamos o claim aqui, num lugar só, em vez
  // de mudar cada controller: o padrão do backend fica preservado.
  const codigoCredenciado = (keycloakService.keycloak.tokenParsed as any)?.codigo_credenciado;
  if (codigoCredenciado) {
    config.headers['X-Usuario'] = String(codigoCredenciado);
  }

  // Mesmo motivo: os endpoints de impressão (ex.: /api/detalha-atendimento/imprimir) leem o nome
  // do usuário de X-Nome-Usuario para carimbar no PDF, com default "Usuario".
  const nomeUsuario = (keycloakService.keycloak.tokenParsed as any)?.given_name;
  if (nomeUsuario) {
    config.headers['X-Nome-Usuario'] = String(nomeUsuario);
  }

  // Overlay global de carregamento (só depois do token; equivale ao p:ajaxStatus do legado).
  // Passe `{ silent: true }` na config da requisição para não acionar o overlay.
  if (!(config as any).silent) {
    (config as any).__loading = true;
    iniciarCarregamento();
  }
  return config;
});

axios.interceptors.response.use(
  (response) => {
    if ((response.config as any)?.__loading) finalizarCarregamento();
    if (concluiAcaoDoUsuario(response.config?.url)) registrarAcaoConcluida();
    return response;
  },
  (error) => {
    if ((error.config as any)?.__loading) finalizarCarregamento();
    return Promise.reject(error);
  }
);

export default axios;
